import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Container, Box, Alert, FormControlLabel, Checkbox } from '@mui/material';
import axios from 'axios';
import AuthSessionManager from './AuthSessionManager';
import { useTheme } from './context/ThemeContext';
import API_BASE_URL from './config/api';
import { CircularProgress } from '@mui/material';
import { usePermissions } from './context/PermissionContext';

function TwoFAPage() {
  const [selectedMethod, setSelectedMethod] = useState(''); // '', 'authApp', 'phone', 'email'
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [trustDevice, setTrustDevice] = useState(true); // default checked — most users want to skip 2FA on familiar devices
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { fetchPermissions } = usePermissions();
  const [otpCode, setotpCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  // AWS End User Messaging DIGITAL_FORM opt-in: SMS consent is a SEPARATE,
  // unchecked checkbox with all required disclosures; Terms/Privacy is its own
  // separate checkbox. Both must be actively checked before we send an SMS.
  const [smsConsent, setSmsConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [loadingState, setLoadingState] = useState({
    authApp: false,
    phone: false,
    email: false,
  });
  // When the user's authenticator is out of sync with the server, they can
  // click "Re-pair my authenticator" to reveal the current QR and rescan it.
  const [repairMode, setRepairMode] = useState(false);

  const {
    qrCode,
    userNo,
    secretKey
  } = AuthSessionManager.get2FASession();

  // Show the QR when the user has picked authApp AND either there's no prior
  // session (brand-new user) OR they've explicitly clicked "Re-pair".
  const showQR = selectedMethod === 'authApp' && qrCode && repairMode;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Construct model based on selected method (you can adjust API accordingly)

      const verifyOTPModel = {
        userNo: userNo,
        otp: code,
        secretKey: selectedMethod === 'authApp' ? secretKey : undefined,
        method: selectedMethod,
        trustDevice: trustDevice,
      };

      // withCredentials: true so the browser accepts the `device_trust` cookie
      // the server sets when trustDevice is checked. Required for CORS on api.theezdocs.com.
      const response = await axios.post(`${API_BASE_URL}/verifyOTP`, verifyOTPModel,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`
          },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        // Replace the short-lived pre-MFA token with the full MFA-verified token
        // returned by /verifyOTP. Without this the stored token expires in 10 min
        // and the user gets bounced out immediately.
        if (response.data.token) {
          localStorage.setItem('jwtToken', response.data.token);
        }
        if (response.data.isAdmin !== undefined) {
          localStorage.setItem('isAdmin', response.data.isAdmin?.toString() || 'false');
        }
        if (response.data.isOwner !== undefined) {
          localStorage.setItem('isOwner', response.data.isOwner?.toString() || 'false');
        }
        if (response.data.accessLevel !== undefined) {
          localStorage.setItem('userAccessLevel', response.data.accessLevel);
        }

        localStorage.setItem('isLoggedIn', 'true');
        // Store 2FA verification per user email
        const userEmail = localStorage.getItem('userEmail')?.toLowerCase();
        if (userEmail) {
          localStorage.setItem(`is2FAVerified_${userEmail}`, 'true');
        }
        AuthSessionManager.clear2FASession();
        // Fetch permissions before navigating to dashboard so admin icon shows immediately
        await fetchPermissions();
        navigate('/dashboard');
      } else {
        setError(response.data.message || 'Invalid 2FA code.');
      }
    } catch (err) {
      setError('Verification failed. Try again.');
    }
  };

  // Reset code & error on method change
const onSelectMethod = async (e, method) => {
  e.preventDefault();
  setError('');

  // For phone method, show phone input first if not already provided
  if (method === 'phone' && !phoneNumber) {
    setShowPhoneInput(true);
    return;
  }

  setLoadingState(prevState => ({ ...prevState, [method]: true }));

  if (method === 'authApp') {
    // For authApp, no API call needed - just show the QR code input
    setSelectedMethod(method);
    setCode('');
    setLoadingState(prevState => ({ ...prevState, [method]: false }));
    return;
  }

  if (method !== 'authApp') {
    const apiEndpoint = method === 'phone' ? 'sendSms' : 'sendEmail';

    // Build request body with userNo and phoneNumber (for SMS)
    const requestBody = { userNo: userNo };
    if (method === 'phone') {
      requestBody.phoneNumber = phoneNumber;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/${apiEndpoint}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
          },
        }
      );

      if (response.data.success) {
        setShowPhoneInput(false);
      } else {
        setError(response.data.message || 'Failed to send code');
        setLoadingState(prevState => ({ ...prevState, [method]: false }));
        return;
      }
    } catch (err) {
      console.error('Error during API call:', err);
      if (err.response) {
        setError(`Error: ${err.response.data.message || 'Failed to send code'}`);
      } else if (err.request) {
        setError('No response from server. Please check your connection or try again later.');
      } else {
        setError('Error occurred while setting up the request.');
      }
      setLoadingState(prevState => ({ ...prevState, [method]: false }));
      return;
    } finally {
      setLoadingState(prevState => ({ ...prevState, [method]: false }));
    }
  }

  setSelectedMethod(method);
  setCode('');
};


  return (
    <Container
      maxWidth="xs"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box
        width="100%"
        padding={4}
        boxShadow={3}
        borderRadius={2}
        bgcolor="background.paper"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" align="center" gutterBottom sx={{ color: 'text.primary' }}>
          Two-Factor Authentication
        </Typography>

        {/* Step 1: Show method selection if none selected */}
        {!selectedMethod && !showPhoneInput && (
          <Box width="100%" display="flex" flexDirection="column" gap={2}>
            <Button variant="contained"
                fullWidth
                disabled={loadingState.authApp}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: '8px',
                  backgroundColor: 'primary.main',
                  color: 'background.paper',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }} onClick={(e) => onSelectMethod(e, 'authApp')}>
              {loadingState.authApp ? (
                <CircularProgress size={24} sx={{ color: 'background.paper' }} />
              ) : (
                'Authorize by Authenticator App'
              )}
            </Button>
            <Button variant="contained"
                fullWidth
                disabled
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: '8px',
                  backgroundColor: 'grey.400',
                  color: 'background.paper',
                  '&:hover': { backgroundColor: 'grey.400' },
                  '&.Mui-disabled': { backgroundColor: 'grey.400', color: 'grey.600' },
                }}>
              Authorize by Phone (Coming Soon)
            </Button>
            <Button variant="contained"
                fullWidth
                disabled={loadingState.email}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: '8px',
                  backgroundColor: 'primary.main',
                  color: 'background.paper',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }} onClick={(e) => onSelectMethod(e, 'email')}>
              {loadingState.email ? (
                <CircularProgress size={24} sx={{ color: 'background.paper' }} />
              ) : (
                'Authorize by Email'
              )}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: '8px',
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
              onClick={() => {
                // Clear session and go back to login
                localStorage.removeItem('jwtToken');
                localStorage.removeItem('isLoggedIn');
                localStorage.removeItem('userEmail');
                AuthSessionManager.clear2FASession();
                navigate('/');
              }}
            >
              Back to Login
            </Button>
          </Box>
        )}

        {/* Phone number input step */}
        {showPhoneInput && !selectedMethod && (
          <Box width="100%" display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
              Enter your phone number to receive a verification code via SMS
            </Typography>
            <TextField
              label="Phone Number"
              fullWidth
              required
              placeholder="+1234567890"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                  '& fieldset': { borderColor: 'secondary.main' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
              }}
              InputProps={{ style: { color: 'text.primary' } }}
            />

            {/* SMS consent — separate, unchecked checkbox with all required
                disclosures (AWS End User Messaging DIGITAL_FORM opt-in). */}
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  size="small"
                  sx={{ pt: 0 }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                  By checking, you consent to receive <strong>one-time passcodes and verification codes</strong> from
                  Safavieh. Message frequency may vary. Message and data rates may apply. Reply HELP for help or STOP to opt-out.
                </Typography>
              }
            />

            {/* Terms/Privacy — a SEPARATE checkbox, never bundled with SMS consent. */}
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={termsConsent}
                  onChange={(e) => setTermsConsent(e.target.checked)}
                  size="small"
                  sx={{ pt: 0 }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                  By checking, I accept the{' '}
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>Terms of Service</Box>
                  {' '}&amp;{' '}
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>Privacy Policy</Box>.
                </Typography>
              }
            />

            {error && <Alert severity="error" sx={{ bgcolor: 'error.main', color: 'background.paper' }}>{error}</Alert>}
            <Button
              variant="contained"
              fullWidth
              disabled={loadingState.phone || !phoneNumber || !smsConsent || !termsConsent}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: '8px',
                backgroundColor: 'primary.main',
                color: 'background.paper',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
              onClick={(e) => onSelectMethod(e, 'phone')}
            >
              {loadingState.phone ? (
                <CircularProgress size={24} sx={{ color: 'background.paper' }} />
              ) : (
                'Send Code'
              )}
            </Button>
            <Button
              variant="text"
              sx={{ mt: 1 }}
              onClick={() => {
                setShowPhoneInput(false);
                setPhoneNumber('');
                setError('');
                setSmsConsent(false);
                setTermsConsent(false);
              }}
            >
              Back to method selection
            </Button>
          </Box>
        )}

        {/* Step 2: Show UI based on selected method */}
        {selectedMethod && (
          <>
            {/* Show QR code if auth app and repair mode is active */}
            {selectedMethod === 'authApp' && showQR && (
              <Box display="flex" flexDirection="column" alignItems="center" my={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, textAlign: 'center', maxWidth: 340 }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.). If you already have an old entry for this site, <strong>delete it first</strong>, then scan — the new code will match.
                </Typography>
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="2FA QR Code"
                  style={{
                    maxWidth: '300px',
                    maxHeight: '300px',
                    width: '100%',
                    height: 'auto',
                    border: '1px solid',
                    borderColor: '#1976d2',
                  }}
                />
              </Box>
            )}

            {/* Re-pair authenticator — reveals the current QR so a user whose
                authenticator app has drifted from the server can rescan. */}
            {selectedMethod === 'authApp' && qrCode && !showQR && (
              <Box display="flex" justifyContent="center" mt={1}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setRepairMode(true)}
                  sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.8rem',
                    '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                >
                  My authenticator isn't working — re-pair it
                </Button>
              </Box>
            )}

            {/* Show input for code */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <TextField
                label={
                  selectedMethod === 'authApp' ? 'Enter code from app' :
                  selectedMethod === 'phone' ? 'Enter phone verification code' :
                  'Enter email verification code'
                }
                fullWidth
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'background.paper',
                    '& fieldset': { borderColor: 'secondary.main' },
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                  '& .MuiInputLabel-root': { color: 'text.secondary' },
                  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                }}
                InputProps={{ style: { color: 'text.primary' } }}
              />
              {error && <Alert severity="error" sx={{ mb: 2, bgcolor: 'error.main', color: 'background.paper' }}>{error}</Alert>}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                  />
                }
                label="Trust this device for 30 days (skip 2FA on future logins)"
                sx={{ mt: 1, color: 'text.secondary', '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: '8px',
                  backgroundColor: 'primary.main',
                  color: 'background.paper',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }}
              >
                Verify
              </Button>
            </form>

            {/* Optional: Back button to choose method again */}
            <Button
              variant="text"
              sx={{ mt: 1 }}
              onClick={() => {
                setSelectedMethod('');
                setLoadingState({ authApp: false, phone: false, email: false });
                setCode('');
                setError('');
              }}
            >
              Choose a different method
            </Button>
            <Button
              variant="text"
              sx={{ mt: 0, color: 'text.secondary' }}
              onClick={() => {
                // Clear session and go back to login
                localStorage.removeItem('jwtToken');
                localStorage.removeItem('isLoggedIn');
                localStorage.removeItem('userEmail');
                AuthSessionManager.clear2FASession();
                navigate('/');
              }}
            >
              Back to Login
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
}

export default TwoFAPage;
